// layouts/dashboard/components/Projects.js

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { keyframes } from "@mui/system";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React examples
import DataTable from "examples/Tables/DataTable";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import PostAddIcon from "@mui/icons-material/PostAdd";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

// Premium Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

function Projects({ posts = [] }) {
  const navigate = useNavigate();
  const [menu, setMenu] = useState(null);

  const openMenu = ({ currentTarget }) => setMenu(currentTarget);
  const closeMenu = () => setMenu(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Define columns with enhanced styling
  const columns = [
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold" color="dark">
          POST TITLE
        </MDTypography>
      ),
      accessor: "title",
      align: "left",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold" color="dark">
          USER
        </MDTypography>
      ),
      accessor: "user",
      align: "left",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold" color="dark">
          PRICE
        </MDTypography>
      ),
      accessor: "price",
      align: "center",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold" color="dark">
          TYPE
        </MDTypography>
      ),
      accessor: "type",
      align: "center",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold" color="dark">
          DATE
        </MDTypography>
      ),
      accessor: "date",
      align: "center",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold" color="dark">
          ACTION
        </MDTypography>
      ),
      accessor: "action",
      align: "center",
    },
  ];

  // Prepare rows with 3D hover effects
  const rows = posts.map((post, index) => ({
    title: (
      <MDTypography
        variant="caption"
        fontWeight="medium"
        sx={{
          transition: "all 0.2s",
          "&:hover": { color: "#2196f3", transform: "translateX(3px)" },
          display: "inline-block",
        }}
      >
        {post.title?.length > 30 ? `${post.title.substring(0, 30)}...` : post.title}
      </MDTypography>
    ),
    user: (
      <MDBox display="flex" alignItems="center" gap={1}>
        <Avatar
          src={post.user?.profile_photo}
          sx={{
            width: 28,
            height: 28,
            border: "1px solid #e0e0e0",
            transition: "all 0.2s",
            "&:hover": { transform: "scale(1.1)", borderColor: "#2196f3" },
          }}
        />
        <MDTypography variant="caption" fontWeight="medium">
          {post.user?.name}
        </MDTypography>
      </MDBox>
    ),
    price: (
      <MDTypography variant="caption" fontWeight="bold" color="error">
        ₹{post.price}
      </MDTypography>
    ),
    type: (
      <Chip
        label={post.post_type?.toUpperCase()}
        size="small"
        sx={{
          bgcolor: post.post_type === "sell" ? "#C62828" : "#2E7D32",
          color: "#fff",
          fontSize: "0.7rem",
          fontWeight: "bold",
          transition: "all 0.2s",
          "&:hover": {
            transform: "scale(1.05)",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
          },
        }}
      />
    ),
    date: (
      <MDTypography variant="caption" fontWeight="medium">
        {formatDate(post.createdAt)}
      </MDTypography>
    ),
    action: (
      <Tooltip title="View Post">
        <IconButton
          size="small"
          color="info"
          onClick={() => navigate(`/post-details/${post._id}`)}
          sx={{
            transition: "all 0.2s",
            "&:hover": {
              transform: "scale(1.2)",
              color: "#1565c0",
            },
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ),
  }));

  const renderMenu = (
    <Menu
      id="simple-menu"
      anchorEl={menu}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={Boolean(menu)}
      onClose={closeMenu}
    >
      <MenuItem onClick={closeMenu}>Action</MenuItem>
      <MenuItem onClick={closeMenu}>Another action</MenuItem>
      <MenuItem onClick={closeMenu}>Something else</MenuItem>
    </Menu>
  );

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: "24px",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        "&:hover": {
          transform: "translateY(-8px) scale(1.01)",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #2196f3, #4caf50, #ff9800, #f44336, #2196f3)",
          animation: `${shimmer} 3s infinite linear`,
          backgroundSize: "200% 100%",
          zIndex: 2,
        },
      }}
    >
      {/* Animated background gradient */}
      <MDBox
        sx={{
          position: "absolute",
          top: -50,
          left: -50,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(33,150,243,0.12) 0%, rgba(33,150,243,0) 70%)",
          pointerEvents: "none",
          animation: `${pulseGlow} 3s infinite`,
        }}
      />

      {/* Header with 3D effect */}
      <MDBox pt={3} px={3} sx={{ position: "relative", zIndex: 1 }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDTypography variant="h6" fontWeight="bold" sx={{ letterSpacing: "-0.5px" }}>
            Recent Posts
          </MDTypography>
          <Tooltip title="View all posts">
            <IconButton
              size="small"
              sx={{
                bgcolor: "rgba(33,150,243,0.1)",
                transition: "all 0.3s",
                "&:hover": {
                  bgcolor: "rgba(33,150,243,0.2)",
                  transform: "rotate(90deg)",
                },
              }}
              onClick={() => navigate("/posts")}
            >
              <PostAddIcon sx={{ color: "#2196f3" }} />
            </IconButton>
          </Tooltip>
        </MDBox>
        <MDBox mt={0} mb={0}>
          <MDTypography variant="button" color="text" fontWeight="regular">
            <MDTypography display="inline" variant="body2" verticalAlign="middle">
              <Icon sx={{ color: "#4caf50" }}>trending_up</Icon>
            </MDTypography>
            &nbsp;
            <MDTypography variant="button" color="text" fontWeight="bold">
              {posts.length}
            </MDTypography>{" "}
            latest posts
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox p={2} sx={{ position: "relative", zIndex: 1, animation: `${slideIn} 0.5s ease-out` }}>
        {posts.length === 0 ? (
          <MDTypography
            variant="body2"
            color="textSecondary"
            textAlign="center"
            sx={{ py: 4, animation: `${fadeInUp} 0.5s ease-out` }}
          >
            No posts yet
          </MDTypography>
        ) : (
          <DataTable
            table={{ columns, rows }}
            showTotalEntries={false}
            isSorted={false}
            noEndBorder
            entriesPerPage={false}
            sx={{
              "& .MuiTableRow-root": {
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: "rgba(33,150,243,0.04)",
                  transform: "scale(1.01)",
                  cursor: "pointer",
                },
              },
              "& .MuiTableCell-root": {
                borderBottomColor: "rgba(0,0,0,0.05)",
              },
            }}
          />
        )}
      </MDBox>

      {/* 3D decorative element */}
      <MDBox
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: 120,
          height: 120,
          background: "radial-gradient(circle, rgba(33,150,243,0.08) 0%, rgba(33,150,243,0) 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />
    </Card>
  );
}

Projects.propTypes = {
  posts: PropTypes.array,
};

export default Projects;
